<template>
    <div v-if="displayItems.length > 0" :class="['pb-4 lg:pb-0', { 'lg:mb-6': isOpenState }]">
        <button
            @click="isOpenState = !isOpenState"
            class="mb-6 flex w-full cursor-pointer items-center justify-between border-b-2 border-[rgb(212_215_202)] px-4 pb-3">
            <span class="font-monument-extended block text-[14px] leading-[33px]">{{ tree.title || 'Categorie' }}</span>
            <IconChevronDown
                :class="{ '!-rotate-180': isOpenState }"
                class="size-3 -rotate-90 transition-all lg:-rotate-0" />
        </button>
        <div
            class="overflow-hidden pt-1.5 lg:px-4 lg:duration-300"
            :class="{ 'filter-open': isOpenState, 'filter-closed': !isOpenState }"
            :aria-hidden="!isOpenState"
            role="group">
            <div class="flex h-full flex-col">
                <div class="flex-1 overflow-y-auto">
                    <div class="mt-3 lg:hidden">
                        <button type="button" class="mb-8 flex items-center leading-none" @click="isOpenState = false">
                            <ArrowLeftIcon class="mr-3 size-5 stroke-2" />
                            Terug
                        </button>
                        <span class="styled-as-h3 mb-5 block text-xl">{{ tree.title || 'Categorie' }}</span>
                    </div>
                    <div class="mb-10 grid grid-cols-1 gap-x-16 lg:mb-0">
                        <template v-for="(item, index) in displayItems" :key="item.categoryPath || item.categoryId">
                            <div
                                :class="[
                                    'mb-4 last:mb-0',
                                    {
                                        'mb-8':
                                            isSpecialItem(item) &&
                                            index < displayItems.length - 1 &&
                                            !isSpecialItem(displayItems[index + 1]),
                                    },
                                ]">
                                <CategoryLink
                                    v-if="categoryTarget(item)"
                                    :to="categoryTarget(item)!"
                                    :category-id="item.categoryId"
                                    :category-path="item.categoryPath"
                                    :search="navigationSearch"
                                    @click="mobileFiltersMenuStore.closeMenu()"
                                    class="mb-0 !flex w-full cursor-pointer items-center text-sm font-normal"
                                    :class="[
                                        { '!font-black': item.isSelected },
                                        { 'pointer-events-none': item.isSelected },
                                    ]">
                                    <span v-if="isSaleItem(item)" class="sale-tag !text-xs">{{ item.title }}</span>
                                    <span v-else class="flex-1 text-left">{{ item.title }}</span>
                                </CategoryLink>
                                <span v-else class="mb-0 !flex w-full items-center text-sm font-normal">
                                    <span v-if="isSaleItem(item)" class="sale-tag !text-xs">{{ item.title }}</span>
                                    <span v-else class="flex-1 text-left">{{ item.title }}</span>
                                </span>
                                <!-- Children (expanded when parent is selected) -->
                                <div
                                    v-if="item.isSelected && item.children && item.children.length > 0"
                                    class="mt-4 border-l-2 border-green-500 pl-4">
                                    <div v-for="child in item.children" :key="child.categoryPath || child.categoryId" class="mb-4 last:mb-0">
                                        <CategoryLink
                                            v-if="categoryTarget(child)"
                                            :to="categoryTarget(child)!"
                                            :category-id="child.categoryId"
                                            :category-path="child.categoryPath"
                                            :search="navigationSearch"
                                            @click="mobileFiltersMenuStore.closeMenu()"
                                            class="mb-0 !flex w-full cursor-pointer items-center text-sm font-normal"
                                            :class="[
                                                { '!font-black': child.isSelected },
                                                { 'pointer-events-none': child.isSelected },
                                            ]">
                                            <span class="flex-1 text-left">{{ child.title }}</span>
                                        </CategoryLink>
                                        <span v-else class="mb-0 !flex w-full items-center text-sm font-normal">
                                            <span class="flex-1 text-left">{{ child.title }}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
                <div class="sticky right-0 bottom-0 left-0 bg-white pb-5 lg:hidden">
                    <button
                        type="button"
                        class="btn w-full bg-black text-white"
                        @click="
                            isOpenState = false;
                            mobileFiltersMenuStore.closeMenu();
                        ">
                        {{ $snippets['headless.plp.view'] }} {{ total }} {{ $snippets['headless.plp.items'] }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ArrowLeftIcon } from '@heroicons/vue/24/outline';
    import type { RouteLocationRaw } from 'vue-router';
    import {
        isSpecialCategoryItem as isSpecialItem,
        isSaleItem,
        renderedTreeItems,
        renderedSearchTreeItems,
        detectActiveVariant,
        resolveManufacturerSeoUrl,
    } from '~/utils/plp';

    interface SearchCategoryItem {
        title: string;
        categoryPath: string;
        categoryId?: string;
        nrOfResults: number | null;
        isSelected: boolean;
        children: SearchCategoryItem[];
    }

    interface SearchCategoryTreeData {
        title: string;
        urlKey: string;
        items: SearchCategoryItem[];
    }

    const props = withDefaults(
        defineProps<{
            tree: SearchCategoryTreeData;
            isOpen?: boolean;
            total?: number;
            seoUrlMap?: Record<string, string>;
            isManufacturerPage?: boolean;
        }>(),
        {
            isOpen: true,
            isManufacturerPage: false,
        },
    );

    const isOpenState = ref(props.isOpen);
    const keepFilters = useKeepFilters();
    const route = useRoute();
    const navigationSource = useState<any>('plp-navigation-source');
    const navigationSearch = computed(() =>
        props.isManufacturerPage
            ? navigationSource.value?.manufacturer
            : String(route.query.tn_q || route.query.search || ''),
    );
    const mobileFiltersMenuStore = useMobileFiltersMenuStore();

    const activeVariant = computed(() =>
        detectActiveVariant(
            route.path,
            typeof route.query.variant === 'string' ? route.query.variant : undefined,
            props.tree?.items,
        ),
    );

    // When a special item (Nieuw/Sale) is selected, treat it as the root to avoid
    // duplicated children showing as both nested items and root-level siblings.
    const displayItems = computed(() => {
        const items = props.isManufacturerPage
            ? renderedTreeItems(props.tree?.items)
            : renderedSearchTreeItems(props.tree?.items);
        if (!keepFilters.value) return items;
        // Match the regular category menu: campaign links precede product categories.
        return [...items].sort((a, b) => Number(isSpecialItem(b)) - Number(isSpecialItem(a)));
    });

    function resolveSeoUrl(item: SearchCategoryItem): string | undefined {
        return resolveManufacturerSeoUrl(item, {
            seoMap: props.seoUrlMap,
            variant: activeVariant.value,
            treeItems: props.tree?.items,
        });
    }

    /**
     * Resolve a tree item to a router target. Rendered as a real CategoryLink href so
     * crawlers can follow the category tree instead of hitting dead <button>s.
     */
    function categoryTarget(item: SearchCategoryItem): string | RouteLocationRaw | undefined {
        // On manufacturer pages, navigate to SEO URLs when available
        if (props.isManufacturerPage) {
            const seoPath = resolveSeoUrl(item);
            if (seoPath) return '/' + seoPath.replace(/^\//, '');
        }

        if (!item.categoryPath) return undefined;

        // Send the path verbatim. Tweakwise addresses a category by its full
        // ancestor chain ("{dames}-{gilets}"); trimming it to the last segment
        // leaves an id it cannot resolve, and the listing comes back empty.
        const tnCid = item.categoryPath;

        let variant: string | undefined;
        if (props.isManufacturerPage) {
            const name = item.title?.toLowerCase();
            if (name === 'sale') variant = 'sale';
            else if (name === 'nieuw' || name === 'new') variant = 'nieuw';
        }

        const newQuery: Record<string, string | undefined> = {
            ...(route.query as Record<string, string>),
            tn_cid: tnCid,
            variant,
            p: '1',
        };

        return { path: route.path, query: newQuery };
    }
</script>
